
'use client'

import {
  useCallback,
  useRef,
  useState,
} from 'react'

import {
  Loader2,
  Users,
} from 'lucide-react'

import type {
  LaunchCommunityChoice,
} from '@/lib/platform/launchCommunities.server'

function initials(name: string) {
  return (
    name
      .trim()
      .split(/\s+/)
      .slice(0, 2)
      .map((part) => part.charAt(0))
      .join('')
      .toUpperCase() || '?'
  )
}

const ICON_SIZE = 88
const MAX_SCALE = 1.48
const INFLUENCE_RADIUS = 170

export function LaunchCommunityPicker({
  communities,
}: {
  communities: LaunchCommunityChoice[]
}) {
  const [opening, setOpening] =
    useState<number | null>(null)

  const [error, setError] =
    useState<string | null>(null)

  const [brokenImages, setBrokenImages] =
    useState<number[]>([])

  const [mouseX, setMouseX] =
    useState<number | null>(null)

  const itemRefs = useRef<
    Record<number, HTMLButtonElement | null>
  >({})

  async function selectCommunity(sellerId: number) {
    if (opening !== null) return

    setOpening(sellerId)
    setError(null)

    try {
      const response = await fetch(
        '/api/communities/switch',
        {
          method: 'POST',
          credentials: 'same-origin',
          cache: 'no-store',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ sellerId }),
        }
      )

      const data = await response
        .json()
        .catch(() => null)

      if (
        !response.ok ||
        !data?.ok ||
        Number(data?.sellerId) !== sellerId
      ) {
        throw new Error(
          'No se pudo abrir esta comunidad. Actualiza la página e inténtalo de nuevo.'
        )
      }

      // Recarga completa para actualizar sesión y providers.
      window.location.assign('/dashboard')
    } catch (err) {
      setError(
        err instanceof Error
          ? err.message
          : 'No se pudo seleccionar la comunidad.'
      )

      setOpening(null)
    }
  }

  const getScale = useCallback(
    (sellerId: number) => {
      if (mouseX === null) return 1

      const element = itemRefs.current[sellerId]
      if (!element) return 1

      const rect = element.getBoundingClientRect()

      const centerX =
        rect.left + rect.width / 2

      const distance =
        Math.abs(mouseX - centerX)

      // Curva suave: el ícono cercano crece más.
      const normalized = Math.min(
        distance / INFLUENCE_RADIUS,
        1
      )

      const influence =
        (Math.cos(normalized * Math.PI) + 1) / 2

      return (
        1 +
        (MAX_SCALE - 1) * influence
      )
    },
    [mouseX]
  )

  return (
    <main
      className="
        relative flex min-h-[100dvh]
        items-center justify-center
        overflow-hidden
        bg-background
        px-4 py-12
        text-foreground
        sm:px-6
      "
    >
      {/* AMBIENT BACKGROUND */}
      <div
        aria-hidden="true"
        className="
          pointer-events-none
          absolute inset-0
          overflow-hidden
        "
      >
        <div
          className="
            absolute left-1/2 top-[40%]
            h-[420px] w-[420px]
            -translate-x-1/2 -translate-y-1/2
            rounded-full
            bg-primary/[0.055]
            blur-[110px]
            sm:h-[650px] sm:w-[650px]
          "
        />
      </div>

      {/* CONTENT */}
      <div
        className="
          relative z-10
          w-full max-w-6xl
          text-center
        "
      >
        {/* HEADER */}
        <div className="mx-auto mb-12 max-w-xl sm:mb-16">
          <div
            className="
              mb-5 inline-flex
              h-12 w-12
              items-center justify-center
              rounded-2xl
              bg-primary/10
              text-primary
            "
          >
            <Users className="h-5 w-5" />
          </div>

          <h1
            className="
              text-[28px] font-semibold
              tracking-[-0.035em]
              sm:text-[38px]
            "
          >
            Tus comunidades
          </h1>

          <p
            className="
              mt-3 text-sm
              leading-relaxed
              text-muted-foreground
              sm:text-base
            "
          >
            Selecciona una comunidad para continuar.
          </p>
        </div>

        {communities.length ? (
          <>
            {/* DESKTOP DOCK */}
            <div
              className="
                hidden md:flex
                justify-center
                px-8 py-10
              "
            >
              <div
                className="
                  flex max-w-full
                  flex-wrap
                  items-end justify-center
                  gap-x-7 gap-y-12
                "
                onMouseMove={(event) => {
                  setMouseX(event.clientX)
                }}
                onMouseLeave={() => {
                  setMouseX(null)
                }}
              >
                {communities.map((community) => {
                  const sellerId =
                    Number(community.sellerId)

                  const scale = getScale(sellerId)

                  const isOpening =
                    opening === sellerId

                  const showLogo =
                    Boolean(community.logoUrl) &&
                    !brokenImages.includes(sellerId)

                  return (
                    <button
                      key={sellerId}
                      ref={(element) => {
                        itemRefs.current[sellerId] =
                          element
                      }}
                      type="button"
                      onClick={() =>
                        void selectCommunity(sellerId)
                      }
                      disabled={opening !== null}
                      aria-label={`Abrir ${community.name}`}
                      className="
                        group relative
                        flex w-[124px]
                        shrink-0 flex-col
                        items-center justify-end
                        gap-4
                        border-0 bg-transparent
                        p-0
                        text-center
                        outline-none
                        focus-visible:rounded-xl
                        focus-visible:ring-2
                        focus-visible:ring-primary
                        disabled:cursor-wait
                      "
                    >
                      {/* ICON AREA */}
                      <span
                        className="
                          relative flex h-[132px]
                          w-full items-end
                          justify-center
                        "
                      >
                        <span
                          className="
                            relative flex
                            items-center justify-center
                            will-change-transform
                            motion-safe:transition-transform
                            motion-safe:duration-200
                            motion-safe:ease-out
                            motion-reduce:transform-none
                          "
                          style={{
                            transform: `scale(${scale})`,
                            transformOrigin: 'center bottom',
                            zIndex: Math.round(
                              scale * 100
                            ),
                          }}
                        >
                          <span
                            className="
                              relative flex
                              h-[88px] w-[88px]
                              items-center justify-center
                              overflow-hidden
                              rounded-[24px]
                              bg-primary/10
                              text-[26px]
                              font-semibold
                              text-primary
                              shadow-[0_10px_28px_rgba(0,0,0,0.16)]
                              ring-1 ring-border/30
                              transition-shadow
                              duration-300
                              group-hover:shadow-[0_18px_40px_rgba(0,0,0,0.24)]
                            "
                          >
                            {isOpening ? (
                              <Loader2
                                className="
                                  h-6 w-6 animate-spin
                                "
                              />
                            ) : showLogo ? (
                              <img
                                src={
                                  community.logoUrl!
                                }
                                alt=""
                                draggable={false}
                                className="
                                  h-full w-full
                                  object-cover
                                "
                                onError={() => {
                                  setBrokenImages(
                                    (prev) =>
                                      prev.includes(
                                        sellerId
                                      )
                                        ? prev
                                        : [
                                            ...prev,
                                            sellerId,
                                          ]
                                  )
                                }}
                              />
                            ) : (
                              initials(
                                community.name
                              )
                            )}
                          </span>
                        </span>
                      </span>

                      {/* LABEL */}
                      <span
                        className="
                          line-clamp-2
                          min-h-[38px]
                          w-full
                          px-1
                          text-[13px]
                          font-medium
                          leading-[19px]
                          text-foreground/80
                          transition-colors
                          duration-200
                          group-hover:text-foreground
                        "
                      >
                        {community.name}
                      </span>

                      {/* DOCK INDICATOR */}
                      <span
                        aria-hidden="true"
                        className="
                          absolute -bottom-2
                          h-1 w-1
                          rounded-full
                          bg-primary
                          opacity-0
                          transition-opacity
                          group-hover:opacity-100
                        "
                      />
                    </button>
                  )
                })}
              </div>
            </div>

            {/* MOBILE DOCK / CAROUSEL */}
            <div className="md:hidden">
              <div
                className={
                  communities.length <= 2
                    ? 'flex justify-center'
                    : 'flex justify-start'
                }
              >
                <div
                  className={`
                    flex max-w-full
                    gap-4
                    px-2 pb-6 pt-4
                    ${
                      communities.length > 2
                        ? 'w-full overflow-x-auto overscroll-x-contain snap-x snap-mandatory scrollbar-hide'
                        : 'w-auto justify-center'
                    }
                  `}
                >
                  {communities.map(
                    (community) => {
                      const sellerId =
                        Number(
                          community.sellerId
                        )

                      const isOpening =
                        opening === sellerId

                      const showLogo =
                        Boolean(
                          community.logoUrl
                        ) &&
                        !brokenImages.includes(
                          sellerId
                        )

                      return (
                        <button
                          key={sellerId}
                          type="button"
                          onClick={() =>
                            void selectCommunity(
                              sellerId
                            )
                          }
                          disabled={
                            opening !== null
                          }
                          aria-label={`Abrir ${community.name}`}
                          className="
                            group flex
                            w-[calc((100vw-64px)/2)]
                            max-w-[160px]
                            shrink-0 snap-center
                            flex-col items-center
                            gap-3
                            border-0 bg-transparent
                            px-2 py-3
                            text-center
                            outline-none
                            focus-visible:rounded-xl
                            focus-visible:ring-2
                            focus-visible:ring-primary
                            disabled:cursor-wait
                          "
                        >
                          <span
                            className="
                              relative flex
                              h-20 w-20
                              items-center justify-center
                              overflow-hidden
                              rounded-[23px]
                              bg-primary/10
                              text-2xl
                              font-semibold
                              text-primary
                              shadow-[0_12px_32px_rgba(0,0,0,0.16)]
                              ring-1 ring-border/30
                              transition-transform
                              duration-300
                              ease-out
                              active:scale-90
                            "
                          >
                            {isOpening ? (
                              <Loader2
                                className="
                                  h-6 w-6
                                  animate-spin
                                "
                              />
                            ) : showLogo ? (
                              <img
                                src={
                                  community.logoUrl!
                                }
                                alt=""
                                draggable={
                                  false
                                }
                                className="
                                  h-full w-full
                                  object-cover
                                "
                                onError={() =>
                                  setBrokenImages(
                                    (prev) =>
                                      prev.includes(
                                        sellerId
                                      )
                                        ? prev
                                        : [
                                            ...prev,
                                            sellerId,
                                          ]
                                  )
                                }
                              />
                            ) : (
                              initials(
                                community.name
                              )
                            )}
                          </span>

                          <span
                            className="
                              line-clamp-2
                              min-h-[40px]
                              max-w-full
                              text-[13px]
                              font-medium
                              leading-5
                            "
                          >
                            {community.name}
                          </span>
                        </button>
                      )
                    }
                  )}
                </div>
              </div>

              {communities.length > 2 && (
                <p
                  className="
                    mt-2 text-center
                    text-xs text-muted-foreground/60
                  "
                >
                  Desliza para ver más comunidades
                </p>
              )}
            </div>
          </>
        ) : (
          /* EMPTY STATE */
          <div
            className="
              mx-auto max-w-md
              rounded-3xl
              border border-border/60
              bg-card/60
              p-8 text-center
            "
          >
            <Users
              className="
                mx-auto h-8 w-8
                text-muted-foreground
              "
            />

            <h2
              className="
                mt-4 font-semibold
              "
            >
              Sin comunidades disponibles
            </h2>

            <p
              className="
                mt-2 text-sm
                text-muted-foreground
              "
            >
              Por ahora no encontramos comunidades
              activas a las que tengas acceso.
            </p>

            <a
              href="/"
              className="
                mt-5 inline-block
                text-sm font-semibold
                text-primary
                hover:underline
              "
            >
              Ir a Hubee
            </a>
          </div>
        )}

        {/* ERROR */}
        {error && (
          <p
            role="alert"
            className="
              mx-auto mt-8
              max-w-lg
              text-center text-sm
              text-destructive
            "
          >
            {error}
          </p>
        )}

        {/* FOOTER */}
        {communities.length > 0 && (
          <p
            className="
              mt-10 text-center
              text-xs
              text-muted-foreground/50
            "
          >
            Tu espacio, tus comunidades.
          </p>
        )}
      </div>
    </main>
  )
}
